// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

import type { ProviderAccessPageContext, ProviderFormSubmissionRequest, ProviderRejectionRequest } from '../types/providerAccess';
import type { WizardStepId, StepFieldDefinition, StepComponentProps, PolicyTypeKey } from '../types/wizard';
import { WIZARD_STEP_NAMES, POLICY_TYPE_VISIBILITY, POLICY_TYPE_KEYS, getActiveStepsForPolicyType } from '../types/wizard';
import { useWizardState } from '../hooks/useWizardState';
import { useWizardStorage } from '../hooks/useWizardStorage';
import { useStepValidation } from '../hooks/useStepValidation';
import { useWizardSubmission } from '../hooks/useWizardSubmission';
import type { WizardSubmissionApi } from '../hooks/useWizardSubmission';
import type { ProviderSubmissionResult } from '../types/providerAccess';
import { WizardLayout } from './WizardLayout';
import { WizardHeader } from './WizardHeader';
import { WizardSidebar } from './WizardSidebar';
import { WizardStepContent } from './WizardStepContent';
import { WizardFooter, WizardCompleteFooter } from './WizardFooter';
import { WizardProgressBar } from './WizardProgressBar';
import { WizardSaveToast } from './WizardSaveToast';
import { WizardStorageBanner } from './WizardStorageBanner';
import { WizardErrorSummary } from './WizardErrorSummary';

// Step components
import { StepLoaValidation } from './steps/StepLoaValidation';
import { StepProviderDetails } from './steps/StepProviderDetails';
import { StepPolicyholderDetails } from './steps/StepPolicyholderDetails';
import { StepPolicyDetails } from './steps/StepPolicyDetails';
import { StepEmploymentDetails } from './steps/StepEmploymentDetails';
import { StepContributionDetails } from './steps/StepContributionDetails';
import { StepTransfersIn } from './steps/StepTransfersIn';
import { StepCrystallisedRetired } from './steps/StepCrystallisedRetired';
import { StepInvestments } from './steps/StepInvestments';
import { StepChargesFees } from './steps/StepChargesFees';
import { StepOtherInformation } from './steps/StepOtherInformation';
import { StepUploadDocuments } from './steps/StepUploadDocuments';
import { StepTransactionHistory } from './steps/StepTransactionHistory';
import { TransactionHistoryTable } from './wizard/TransactionHistoryTable';
import { StepAssociatedBenefits } from './steps/StepAssociatedBenefits';
import { StepUploads } from './steps/StepUploads';
import { StepOtherChargesDetails } from './steps/StepOtherChargesDetails';
import { extractDocument, applyExtraction } from '../extraction';
import { toIsoDate } from '../utils/date';
import type { ExtractedDocument, ExtractionRunResult } from '../types/extraction';
import type { TransactionHistoryGroup, AutoFilledMap } from '../types/wizard';


// ========================================================================
// Field Definitions for Validation
// ========================================================================

// Per-fund required fields for the Investments step. Funds themselves are
// optional (the user may add zero), but if any fund is added these fields must
// be filled. Sharon/Ahmed (2026 PRSA review) made `totalExpenseRatio`,
// `performanceFees`, `bidOfferSpread` and `marketValueAdjustment` non-mandatory
// — performance fees in particular don't apply to PRSA, and the others are
// often sourced from a Statement of Investment Disclosures (SID) rather than
// keyed in by the advisor. Exported so tests can lock the contract.
export const INVESTMENT_FUND_REQUIRED_FIELDS = [
  'fundName',
  'allocationPercent',
  'riskRating',
  'annualManagementCharge',
] as const;

const STEP_FIELD_DEFINITIONS: Partial<Record<WizardStepId, StepFieldDefinition[]>> = {
  'loa-validation': [
    { key: 'fullPolicyNumber', label: 'Policy number', type: 'text', required: true, maxLength: 100 },
    { key: 'policyCategory', label: 'Type of policy', type: 'dropdown', required: true },
    { key: 'pensionType', label: 'Pension type', type: 'dropdown', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'investmentType', label: 'Investment type', type: 'dropdown', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
    { key: 'policyStatus', label: 'Policy status', type: 'dropdown', required: true },
    { key: 'policyClosedReason', label: 'Closed status', type: 'dropdown', required: false, requiredWhen: { field: 'policyStatus', value: 'CLOSED' } },
  ],
  'policy-info': [
    { key: 'fullPolicyNumber', label: 'Policy number', type: 'text', required: true, maxLength: 100 },
    { key: 'policyCategory', label: 'Type of policy', type: 'dropdown', required: true },
    { key: 'pensionType', label: 'Pension type', type: 'dropdown', required: true },
    { key: 'policyStatus', label: 'Policy status', type: 'dropdown', required: true },
    { key: 'policyClosedReason', label: 'Closed status', type: 'dropdown', required: false, requiredWhen: { field: 'policyStatus', value: 'CLOSED' } },
  ],
  'provider-details': [
    { key: 'providerContactName', label: 'Name of provider', type: 'text', required: true, maxLength: 255 },
    { key: 'completedByName', label: 'Completed by', type: 'text', required: true, maxLength: 255 },
    { key: 'completedByEmail', label: 'Email contact', type: 'email', required: true, maxLength: 255 },
  ],
  'policyholder-details': [
    { key: 'policyholderName', label: 'Name', type: 'text', required: true, maxLength: 255 },
    { key: 'policyholderDateOfBirth', label: 'Date of birth', type: 'text', required: true, maxLength: 10 },
    { key: 'policyholderEmail', label: 'Email', type: 'email', required: true, maxLength: 255 },
    { key: 'policyholderPhone', label: 'Phone', type: 'phone', required: true, maxLength: 50 },
    { key: 'policyholderCountryCode', label: 'Country', type: 'dropdown', required: true },
    { key: 'policyholderAddressLine1', label: 'Address line 1', type: 'text', required: true, maxLength: 255 },
    { key: 'policyholderAddressLine2', label: 'Address line 2', type: 'text', required: true, maxLength: 255 },
    { key: 'policyholderAddressLine3', label: 'Address line 3', type: 'text', required: false, maxLength: 255 },
    { key: 'policyholderCity', label: 'City', type: 'text', required: true, maxLength: 100 },
  ],
  'uploads': [],
  'other-charges-details': [
    // Investment-only fields
    { key: 'guaranteedReturnsDetails', label: 'Guaranteed Returns Details', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
    { key: 'terminalBonusDetails', label: 'Terminal Bonus Details', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
    { key: 'otherChargeDetails', label: 'Other Charge Details', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
  ],
  'policy-details': [
    { key: 'policyType', label: 'Policy type', type: 'dropdown', required: true },
    { key: 'fullPolicyNumber', label: 'Policy number', type: 'text', required: true, maxLength: 100 },
    // Pension-specific fields
    { key: 'currentValue', label: 'Current value', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'currentValueDate', label: 'Date as at current value', type: 'date', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'transferValue', label: 'Transfer value', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'transferValueDate', label: 'Date as at transfer value', type: 'date', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    // Investment-specific fields
    { key: 'currentPolicyValue', label: 'Current / Policy value', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
    { key: 'dateCurrentPolicyValue', label: 'Date as at current/policy value', type: 'date', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
    { key: 'exitPenaltyDetails', label: 'Exit penalty details', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
    { key: 'transferSurrenderValue', label: 'Transfer/Surrender value', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
    { key: 'dateTransferSurrenderValue', label: 'Date as at transfer/surrender value', type: 'date', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
  ],
  'employment-details': [
    { key: 'employerName', label: 'Employer name', type: 'text', required: true, maxLength: 255 },
    { key: 'dateJoinedScheme', label: 'Date joined scheme', type: 'date', required: true },
    { key: 'firstDateOfEmployment', label: 'First date of employment', type: 'date', required: true },
    { key: 'schemePolicyNra', label: 'Scheme/policy NRA', type: 'text', required: true, maxLength: 10 },
    { key: 'dateOfLeavingService', label: 'Date of leaving service', type: 'date', required: false },
    { key: 'salary', label: 'Salary at leaving service', type: 'currency', required: false },
  ],
  'contribution-details': [
    { key: 'employerContribution', label: 'Employer Contribution to date', type: 'currency', required: true },
    { key: 'employeeContribution', label: 'Employee Contribution to date', type: 'currency', required: true },
    { key: 'payingStatus', label: 'Paying status', type: 'dropdown', required: true },
    { key: 'dateOfLastContribution', label: 'Date of last contribution', type: 'date', required: true },
    // Sharon/Ahmed (2026 PRSA review): no longer mandatory at save time even
    // when payingStatus = "In Force". Source-of-truth for premiums shifts to
    // the transactions document; advisors must not be blocked when extraction
    // doesn't surface them. Field declarations stay so the wizard still
    // renders/persists them — the `requiredWhen` clause is what's removed.
    { key: 'regularPremium', label: 'Regular Premium', type: 'currency', required: false },
    { key: 'regularPremiumFrequency', label: 'Regular Premium frequency', type: 'dropdown', required: false },
    { key: 'singlePremium', label: 'Single Premium', type: 'currency', required: false },
    { key: 'singlePremiumFrequency', label: 'Single Premium frequency', type: 'dropdown', required: false },
    { key: 'policyPaidBy', label: 'Is the policy paid by', type: 'dropdown', required: false },
    { key: 'dueDateNextPayment', label: 'Due date of next payment', type: 'date', required: false },
    { key: 'dueAmountNextPayment', label: 'Due amount of next payment', type: 'currency', required: false },
    { key: 'transfersInFromAnotherScheme', label: 'Transfers in from another scheme', type: 'currency', required: true },
  ],
  'transfers-in': [
    { key: 'hasReceivedTransfers', label: 'Has the policy received any transfers?', type: 'dropdown', required: true },
    // Details of transfers in (when hasReceivedTransfers = Yes)
    { key: 'detailAmountTransferred', label: 'Amount Transferred in', type: 'currency', required: false, requiredWhen: { field: 'hasReceivedTransfers', value: 'Yes' } },
    { key: 'detailProvider', label: 'Policy/Account Provider', type: 'dropdown', required: false, requiredWhen: { field: 'hasReceivedTransfers', value: 'Yes' } },
    { key: 'detailDateTransferred', label: 'Date Transferred in', type: 'date', required: false, requiredWhen: { field: 'hasReceivedTransfers', value: 'Yes' } },
    { key: 'detailPolicyType', label: 'Policy/Account/Pension Type', type: 'dropdown', required: false, requiredWhen: { field: 'hasReceivedTransfers', value: 'Yes' } },
    { key: 'detailReferenceNumber', label: 'Policy/Account Reference Number', type: 'text', required: false, requiredWhen: { field: 'hasReceivedTransfers', value: 'Yes' } },
    { key: 'detailSchemeName', label: 'Name of Scheme transfer received from', type: 'text', required: false, requiredWhen: { field: 'hasReceivedTransfers', value: 'Yes' } },
    { key: 'sameEmployment', label: 'Was the transfer in relating to the same employment', type: 'dropdown', required: false, requiredWhen: { field: 'hasReceivedTransfers', value: 'Yes' } },
    // Same employment = Yes
    { key: 'sameEmpDateTransferred', label: 'Date transferred in', type: 'date', required: false, requiredWhen: { field: 'sameEmployment', value: 'Yes' } },
    { key: 'sameEmpAmountTransferred', label: 'Amount transferred in', type: 'currency', required: false, requiredWhen: { field: 'sameEmployment', value: 'Yes' } },
    { key: 'employeeContributionsTransferred', label: 'Employee Contributions Transferred Over', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'Yes' } },
    { key: 'employerContributionsTransferred', label: 'Employer contributions Transferred Over', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'Yes' } },
    { key: 'previousProviderName', label: 'Previous providers name', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'Yes' } },
    { key: 'previousPolicyNumber', label: 'Previous policy number', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'Yes' } },
    { key: 'transferType', label: 'Type of Transfer', type: 'dropdown', required: false, requiredWhen: { field: 'sameEmployment', value: 'Yes' } },
    // Same employment = No
    { key: 'previousProvidersName', label: 'Previous providers name', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'previousPolicyNumberNo', label: 'Previous policy number', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'employerDetails', label: 'Employer details', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'transferDateJoinedService', label: 'Date Joined service', type: 'date', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'transferDateJoinedScheme', label: 'Date Joined Scheme', type: 'date', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'transferDateOfLeavingService', label: 'Date of Leaving Service', type: 'date', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'salaryAtLeavingService', label: 'Salary as at leaving service', type: 'currency', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'totalEmployerContributions', label: 'Total Employer contributions', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'totalEmployeeContributions', label: 'Total Employee Contributions', type: 'text', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    { key: 'transferFromOverseas', label: 'Did the transfer come from overseas?', type: 'dropdown', required: false, requiredWhen: { field: 'sameEmployment', value: 'No' } },
    // Overseas (when transferFromOverseas = Yes)
    { key: 'overseasCountry', label: 'What country did the overseas pension come from?', type: 'dropdown', required: false, requiredWhen: { field: 'transferFromOverseas', value: 'Yes' } },
    { key: 'wasTaxPaid', label: 'Was there tax paid on transfer out?', type: 'dropdown', required: false, requiredWhen: { field: 'transferFromOverseas', value: 'Yes' } },
    { key: 'overseasTaxDetails', label: 'Please provide details and backup', type: 'text', required: false, requiredWhen: { field: 'wasTaxPaid', value: 'Yes' } },
  ],
  'crystallised-retired': [
    { key: 'crystallisedStatus', label: 'Has the policy been crystallised/retired?', type: 'dropdown', required: true },
    { key: 'crystallisedBceDate', label: 'Date of Benefit Crystallisation Event', type: 'date', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedBceValue', label: 'Value of policy as a BCE', type: 'currency', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedProviderName', label: 'Provider name who Crystallised the policy', type: 'dropdown', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedQfmName', label: 'Name of Qualifying Fund Manager (QFM)', type: 'text', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedTaxFreeLumpSum', label: 'Tax Free Lump Sum', type: 'currency', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedTaxFreeLumpSumDate', label: 'Date of Tax Free', type: 'date', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedOtherLumpSum', label: 'Other Lump Sum', type: 'currency', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedTaxPaidOtherLumpSum', label: 'Tax Paid On Other Lump Sum', type: 'currency', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedOtherLumpSumDate', label: 'Date of Lump Sum', type: 'date', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedDrawingIncome', label: 'Is the Policyholder drawing income from the policy?', type: 'dropdown', required: false, requiredWhen: { field: 'crystallisedStatus', value: 'Yes' } },
    { key: 'crystallisedAnnualDrawdownPercent', label: 'Annual Drawdown %', type: 'text', required: false, requiredWhen: { field: 'crystallisedDrawingIncome', value: 'Yes' } },
    { key: 'crystallisedLastAnnualDrawdown', label: 'Last Annual Drawdown', type: 'currency', required: false, requiredWhen: { field: 'crystallisedDrawingIncome', value: 'Yes' } },
    { key: 'crystallisedDrawdownFrequency', label: 'Drawdown Payment Frequency', type: 'dropdown', required: false, requiredWhen: { field: 'crystallisedDrawingIncome', value: 'Yes' } },
    { key: 'crystallisedDrawdownDate', label: 'Date of Drawdown Payment', type: 'date', required: false, requiredWhen: { field: 'crystallisedDrawingIncome', value: 'Yes' } },
  ],
  'investments': [],
  'charges-fees': [
    // Shared fields (investment + pension)
    { key: 'brokerAdvisoryAmc', label: 'Fund Management Charge/ Broker Trail', type: 'text', required: true },
    { key: 'commissionsOnPolicy', label: 'Commissions on policy or account', type: 'text', required: true },
    { key: 'allocationPercentContributions', label: 'Allocation % of Contributions', type: 'text', required: true },
    // Pension-only fields
    { key: 'exitChargesDetails', label: 'Exit Charges/ penalties Details', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'transferOutCosts', label: 'Transfer out costs', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'guaranteedReturnsDetails', label: 'Guaranteed Returns Details', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'terminalBonusDetails', label: 'Terminal Bonus Details', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'otherChargeDetails', label: 'Other Charge Details', type: 'text', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
  ],
  'other-information': [
    // Shared fields (both pension and investment)
    { key: 'policyAssigned', label: 'Has policy been assigned?', type: 'dropdown', required: true },
    { key: 'policyAssignedTo', label: 'To whom?', type: 'text', required: false, maxLength: 255 },
    { key: 'policyAssignedDate', label: 'Date the policy was assigned', type: 'date', required: false },
    // Pension-only fields
    { key: 'pensionAdjustmentOrder', label: 'Pension Adjustment Order', type: 'dropdown', required: false, requiredWhen: { field: 'policyCategory', value: 'pension' } },
    { key: 'pensionAdjustmentDate', label: 'Date of the Pension Adjustment Order', type: 'date', required: false, requiredWhen: { field: 'pensionAdjustmentOrder', value: 'Yes' } },
    // Investment-only fields
    { key: 'hasBareTrustOrDeed', label: 'Has policy a bare trust or deed of assignment?', type: 'dropdown', required: false, requiredWhen: { field: 'policyCategory', value: 'investment' } },
    { key: 'bareTrustOrDeedTo', label: 'To whom?', type: 'text', required: false, maxLength: 255 },
    // Shared field
    { key: 'uniqueDeathBenefit', label: 'Is there a unique death benefit applying to the policy?', type: 'dropdown', required: true },
    { key: 'uniqueDeathBenefitExpiry', label: 'Expiry date of the unique death benefit', type: 'date', required: false, requiredWhen: { field: 'uniqueDeathBenefit', value: 'Yes' } },
  ],
  'upload-documents': [],
  'transaction-history': [],
  'associated-benefits': [
    { key: 'hasProtectionBenefits', label: 'Does the policyholder have any attaching protection benefits?', type: 'dropdown', required: true },
  ],
};

// ========================================================================
// Component
// ========================================================================

interface ProviderAccessWizardProps {
  token: string;
  context: ProviderAccessPageContext;
  submitting: boolean;
  formOpenDuration: number;
  onSubmitForm: (data: ProviderFormSubmissionRequest) => Promise<boolean>;
  onUploadDocument: (file: File, stepId?: string, fieldKey?: string) => Promise<boolean>;
  onRejectLoa: (request: ProviderRejectionRequest) => Promise<boolean>;
}

export function ProviderAccessWizard({
  token,
  context,
  submitting,
  formOpenDuration,
  onSubmitForm,
  onUploadDocument,
  onRejectLoa,
}: ProviderAccessWizardProps) {
  // ========================================================================
  // Policy-type-driven dynamic step list
  // ========================================================================
  const storage = useWizardStorage(token);

  // Derive initial policy type from stored data (so active steps are correct on resume)
  const [selectedPolicyType, setSelectedPolicyType] = useState<PolicyTypeKey | null>(() => {
    const storedType = storage.storageData?.steps?.['policy-details']?.data?.policyType;
    if (typeof storedType === 'string' && (POLICY_TYPE_KEYS as readonly string[]).includes(storedType)) {
      return storedType as PolicyTypeKey;
    }
    return null;
  });

  // Track policy status to hide steps when policy is closed.
  // Whitelisted providers store this on policy-info; others on loa-validation.
  const [selectedPolicyStatus, setSelectedPolicyStatus] = useState<string | null>(() => {
    const stored =
      storage.storageData?.steps?.['policy-info']?.data?.policyStatus ??
      storage.storageData?.steps?.['loa-validation']?.data?.policyStatus;
    return (stored as string) || null;
  });

  // Track policy category (pension vs investment) for step visibility
  const [selectedPolicyCategory, setSelectedPolicyCategory] = useState<string | null>(() => {
    const storedCategory = storage.storageData?.steps?.['loa-validation']?.data?.policyCategory;
    return (storedCategory as string) || context.policyTypeCategory || null;
  });

  // The PDF statement scan flow is currently enabled for Standard Life and
  // any "Test Provider" (e.g. "Finerty Test Provider"). Hide the Upload
  // Documents step for everyone else.
  const providerNameLower = (context.providerName ?? '').trim().toLowerCase();
  const uploadFlowEnabled =
    providerNameLower === 'standard life' || providerNameLower.includes('test provider');

  // Progressive reveal: for whitelisted providers the sidebar only shows
  // LOA validation + Upload documents until the user has completed the
  // upload step. Once the scan is done, the rest of the steps unlock.
  const [uploadStepCompleted, setUploadStepCompleted] = useState<boolean>(
    () => storage.storageData?.steps?.['upload-documents']?.completed === true,
  );

  const activeSteps = useMemo(
    () => {
      const steps = getActiveStepsForPolicyType(selectedPolicyType, selectedPolicyStatus, selectedPolicyCategory);

      // Investment flow never uses the statement-scanner upload step,
      // so skip the whitelisted progressive-reveal entirely. Without this
      // short-circuit the whitelist filter below collapses the active step
      // list to just `loa-validation` for whitelisted providers, because
      // INVESTMENT_STEP_ORDER does not contain `upload-documents`.
      const isInvestment = (selectedPolicyCategory ?? context.policyTypeCategory ?? '')
        .toUpperCase() === 'INVESTMENT';
      if (isInvestment) {
        // INVESTMENT_STEP_ORDER already excludes upload-documents and policy-info.
        return steps;
      }

      if (!uploadFlowEnabled) {
        // Non-whitelisted providers: keep legacy behaviour — no
        // upload-documents and no policy-info (those fields live inside
        // loa-validation for them).
        return steps.filter((s) => s !== 'upload-documents' && s !== 'policy-info');
      }
      // Whitelisted providers: only LOA validation and Upload documents
      // are visible until the user completes the upload. Policy info and
      // the rest unlock together once the scan is done.
      if (!uploadStepCompleted) {
        return steps.filter(
          (s) => s === 'loa-validation' || s === 'upload-documents',
        );
      }
      return steps;
    },
    [selectedPolicyType, selectedPolicyStatus, selectedPolicyCategory, uploadFlowEnabled, uploadStepCompleted, context.policyTypeCategory],
  );

  // Visibility helpers for conditional sub-sections
  const visibility = selectedPolicyType ? POLICY_TYPE_VISIBILITY[selectedPolicyType] : null;
  const showSeveranceSubSection = visibility?.severanceSubSection ?? false;

  // ========================================================================
  // Wizard state & hooks (driven by activeSteps)
  // ========================================================================
  const initialStep = storage.storageData?.currentStep;
  const initialCompleted = useMemo(() => {
    if (!storage.storageData?.steps) return undefined;
    const set = new Set<WizardStepId>();
    for (const [stepId, stepData] of Object.entries(storage.storageData.steps)) {
      if (stepData?.completed) {
        set.add(stepId as WizardStepId);
      }
    }
    return set.size > 0 ? set : undefined;
  }, [storage.storageData]);

  const wizardState = useWizardState(activeSteps, initialStep, initialCompleted);
  // For whitelisted providers, the LOA validation step only asks the
  // Yes/No gate — the policy-number / category / pension-type fields are
  // hidden and must not be validated.
  const effectiveFieldDefinitions = useMemo(() => {
    const isInvestment = (selectedPolicyCategory ?? context.policyTypeCategory ?? '')
      .toUpperCase() === 'INVESTMENT';

    // Investment "Other information" only renders 3 dropdowns
    // (policyAssigned, hasBareTrustOrDeed, uniqueDeathBenefit). The pension
    // STEP_FIELD_DEFINITIONS list contains `uniqueDeathBenefitExpiry` with a
    // requiredWhen on uniqueDeathBenefit=Yes — that field has no UI in the
    // investment flow, so we strip it from the validation set to keep
    // handleComplete's validateAllSteps in sync with what the user can see.
    const investmentOtherInformation: StepFieldDefinition[] = [
      { key: 'policyAssigned', label: 'Has policy/account been assigned or pledged?', type: 'dropdown', required: true },
      { key: 'policyAssignedTo', label: 'To whom?', type: 'text', required: false, maxLength: 255 },
      { key: 'policyAssignedDate', label: 'Date the policy was assigned', type: 'date', required: false },
      { key: 'hasBareTrustOrDeed', label: 'Has policy a bare trust or deed of assignment?', type: 'dropdown', required: true },
      { key: 'bareTrustOrDeedTo', label: 'To whom?', type: 'text', required: false, maxLength: 255 },
      { key: 'uniqueDeathBenefit', label: 'Is there a unique death benefit applying to the policy?', type: 'dropdown', required: true },
    ];

    let defs = STEP_FIELD_DEFINITIONS;
    if (uploadFlowEnabled) {
      defs = { ...defs, 'loa-validation': [] };
    }
    if (isInvestment) {
      defs = { ...defs, 'other-information': investmentOtherInformation };
    }
    return defs;
  }, [uploadFlowEnabled, selectedPolicyCategory, context.policyTypeCategory]);
  const validation = useStepValidation(effectiveFieldDefinitions);
  const noopApi: WizardSubmissionApi = useMemo(() => ({
    submitForm: async () => ({} as ProviderSubmissionResult),
    uploadDocument: async () => ({} as ProviderSubmissionResult),
    extractError: () => ({}),
  }), []);
  const submission = useWizardSubmission(noopApi);

  // ========================================================================
  // Local state per step
  // ========================================================================
  const [stepDataMap, setStepDataMap] = useState<Partial<Record<WizardStepId, Record<string, unknown>>>>(() => {
    if (!storage.storageData?.steps) return {};
    const map: Partial<Record<WizardStepId, Record<string, unknown>>> = {};
    for (const [stepId, stepData] of Object.entries(storage.storageData.steps)) {
      if (stepData?.data) {
        map[stepId as WizardStepId] = stepData.data;
      }
    }
    return map;
  });

  const [pendingFiles, setPendingFiles] = useState<Map<string, File[]>>(new Map());
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  // ========================================================================
  // Statement extractor state (Standard Life Synergy PRSA)
  // ========================================================================
  const [transactionHistory, setTransactionHistoryState] = useState<TransactionHistoryGroup | undefined>(
    () => storage.storageData?.transactionHistory,
  );
  const [autoFilled, setAutoFilledState] = useState<AutoFilledMap>(
    () => storage.storageData?.autoFilled ?? {},
  );

  const currentStepId = wizardState.state.currentStepId;
  const currentStepData = useMemo(() => stepDataMap[currentStepId] || {}, [stepDataMap, currentStepId]);

  // ========================================================================
  // Policy type change handler (called from StepPolicyDetails)
  // ========================================================================
  const handlePolicyTypeChange = useCallback(
    (policyType: PolicyTypeKey | null) => {
      const prevType = selectedPolicyType;
      setSelectedPolicyType(policyType);

      if (!policyType || !prevType) return;

      // Determine which steps were active before and are no longer active
      const prevSteps = getActiveStepsForPolicyType(prevType, selectedPolicyStatus, selectedPolicyCategory);
      const nextSteps = getActiveStepsForPolicyType(policyType, selectedPolicyStatus, selectedPolicyCategory);
      const removedSteps = prevSteps.filter((s) => !nextSteps.includes(s));

      if (removedSteps.length === 0) return;

      // Clear data for removed steps
      setStepDataMap((prev) => {
        const updated = { ...prev };
        for (const stepId of removedSteps) {
          delete updated[stepId];
        }
        return updated;
      });

      // Clear completion status for removed steps
      for (const stepId of removedSteps) {
        wizardState.markStepIncomplete(stepId);
      }
    },
    [selectedPolicyType, wizardState],
  );

  // Keep selectedPolicyType in sync if user changes it in step data
  useEffect(() => {
    const policyDetailsData = stepDataMap['policy-details'];
    const dataType = policyDetailsData?.policyType as string | undefined;
    if (
      dataType &&
      (POLICY_TYPE_KEYS as readonly string[]).includes(dataType) &&
      dataType !== selectedPolicyType
    ) {
      setSelectedPolicyType(dataType as PolicyTypeKey);
    }
  }, [stepDataMap, selectedPolicyType]);

  // Keep selectedPolicyStatus in sync if user changes it in either the
  // LOA validation step (legacy) or the Policy Info step (whitelisted).
  useEffect(() => {
    const piStatus = stepDataMap['policy-info']?.policyStatus as string | undefined;
    const loaStatus = stepDataMap['loa-validation']?.policyStatus as string | undefined;
    const dataStatus = piStatus ?? loaStatus ?? null;
    if (dataStatus !== selectedPolicyStatus) {
      setSelectedPolicyStatus(dataStatus);
    }
  }, [stepDataMap, selectedPolicyStatus]);

  // Keep selectedPolicyCategory in sync if user changes it in LOA validation step
  useEffect(() => {
    const loaData = stepDataMap['loa-validation'];
    const dataCategory = (loaData?.policyCategory as string) || null;
    if (dataCategory && dataCategory !== selectedPolicyCategory) {
      setSelectedPolicyCategory(dataCategory);
    }
  }, [stepDataMap, selectedPolicyCategory]);

  // Ensure policyCategory is always present in step data for conditional validation
  useEffect(() => {
    const category = selectedPolicyCategory || context.policyTypeCategory?.toLowerCase() || null;
    if (!category) return;

    const stepsNeedingCategory: readonly string[] = ['policy-details', 'charges-fees', 'other-charges-details', 'other-information'];
    let needsUpdate = false;
    for (const stepId of stepsNeedingCategory) {
      const stepData = stepDataMap[stepId as WizardStepId];
      if (!stepData || !stepData.policyCategory) {
        needsUpdate = true;
        break;
      }
    }

    if (needsUpdate) {
      setStepDataMap((prev) => {
        const next = { ...prev };
        for (const stepId of stepsNeedingCategory) {
          const id = stepId as WizardStepId;
          const existing = next[id] || {};
          if (!existing.policyCategory) {
            next[id] = { ...existing, policyCategory: category };
          }
        }
        return next;
      });
    }
  }, [stepDataMap, selectedPolicyCategory, context.policyTypeCategory]);

  // ========================================================================
  // Handlers
  // ========================================================================

  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      setStepDataMap((prev) => ({
        ...prev,
        [currentStepId]: {
          ...(prev[currentStepId] || {}),
          [field]: value,
        },
      }));
      validation.clearFieldError(field);
    },
    [currentStepId, validation],
  );

  const handleClearFieldError = useCallback(
    (field: string) => {
      validation.clearFieldError(field);
    },
    [validation],
  );

  const handleAddFile = useCallback(
    (fieldKey: string, file: File) => {
      setPendingFiles((prev) => {
        const next = new Map(prev);
        const existing = next.get(fieldKey) || [];
        next.set(fieldKey, [...existing, file]);
        return next;
      });
      storage.addPendingUpload({
        stepId: currentStepId,
        fieldKey,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
    },
    [currentStepId, storage],
  );

  const handleRemoveFile = useCallback(
    (fieldKey: string, index?: number) => {
      setPendingFiles((prev) => {
        const next = new Map(prev);
        const existing = next.get(fieldKey) || [];
        if (index !== undefined && existing.length > 1) {
          const updated = existing.filter((_, i) => i !== index);
          next.set(fieldKey, updated);
        } else {
          next.delete(fieldKey);
        }
        return next;
      });
      storage.removePendingUpload(currentStepId, fieldKey);
    },
    [currentStepId, storage],
  );

  const saveCurrentStep = useCallback(
    (completed: boolean) => {
      const data = stepDataMap[currentStepId] || {};
      storage.saveStepData(currentStepId, data, completed);
    },
    [currentStepId, stepDataMap, storage],
  );

  const handleSave = useCallback(() => {
    saveCurrentStep(false);
  }, [saveCurrentStep]);

  const handleSaveAndContinue = useCallback(() => {
    // Persist current data as draft
    saveCurrentStep(false);

    // Step 0 (loa-validation): check gate + validate policy fields
    if (currentStepId === 'loa-validation') {
      const loaAccepted = currentStepData.loaAccepted;
      if (loaAccepted !== true) return;

      const isInvestment = context.policyTypeCategory?.toUpperCase() === 'INVESTMENT';

      // Update category state so steps re-render correctly.
      // For investment, the policy details section is hidden so policyCategory
      // may not be in step data — fall back to the backend context value.
      const category = (currentStepData.policyCategory as string)
        || context.policyTypeCategory?.toLowerCase()
        || null;
      setSelectedPolicyCategory(category);

      // Whitelisted providers hide the Policy Details section, so skip
      // field validation entirely — Yes is sufficient to proceed.
      // Investment policies also skip LOA-step field validation.
      if (!isInvestment && !uploadFlowEnabled) {
        // Pension flow: validate the policy detail fields shown when Yes is selected
        const result = validation.validateStep(currentStepId, currentStepData);
        if (!result.valid) {
          wizardState.markStepError(currentStepId);
          errorSummaryRef.current?.focus();
          return;
        }

        // Copy LOA policy fields to policy-details step so they appear pre-filled
        const resolvedPolicyType = category === 'investment'
          ? currentStepData.investmentType
          : currentStepData.pensionType;
        setStepDataMap((prev) => ({
          ...prev,
          'policy-details': {
            ...(prev['policy-details'] || {}),
            fullPolicyNumber: currentStepData.fullPolicyNumber,
            policyCategory: category,
            policyType: resolvedPolicyType,
          },
        }));
      } else {
        // Investment flow: copy category and type to policy-details step
        setStepDataMap((prev) => ({
          ...prev,
          'policy-details': {
            ...(prev['policy-details'] || {}),
            policyCategory: category,
          },
        }));
      }

      wizardState.markStepComplete(currentStepId);
      saveCurrentStep(true);
      wizardState.goNext();
      const nextIndex = wizardState.state.currentStepIndex + 1;
      if (nextIndex < activeSteps.length) {
        storage.updateCurrentStep(activeSteps[nextIndex]);
      }
      validation.clearErrors();
      return;
    }

    // policy-info (whitelisted providers): validate the policy fields and
    // copy them to the downstream policy-details step.
    if (currentStepId === 'policy-info') {
      const result = validation.validateStep(currentStepId, currentStepData);
      if (!result.valid) {
        wizardState.markStepError(currentStepId);
        errorSummaryRef.current?.focus();
        return;
      }
      setStepDataMap((prev) => {
        const existingPd = prev['policy-details'] || {};
        const pensionVal = currentStepData.pensionType as string | undefined;
        const isValidPolicyTypeKey = pensionVal && (POLICY_TYPE_KEYS as readonly string[]).includes(pensionVal);
        return {
          ...prev,
          'policy-details': {
            ...existingPd,
            fullPolicyNumber: currentStepData.fullPolicyNumber,
            policyCategory: currentStepData.policyCategory,
            // Only overwrite policyType when pensionType is a recognised
            // PolicyTypeKey — otherwise keep whatever the scanner set.
            ...(isValidPolicyTypeKey ? { policyType: pensionVal } : {}),
          },
        };
      });
      wizardState.markStepComplete(currentStepId);
      saveCurrentStep(true);
      wizardState.goNext();
      const nextIndex = wizardState.state.currentStepIndex + 1;
      if (nextIndex < activeSteps.length) {
        storage.updateCurrentStep(activeSteps[nextIndex]);
      }
      validation.clearErrors();
      return;
    }

    // Investment other-information: skip generic validation (only 3 selects required)
    if (currentStepId === 'other-information' && context.policyTypeCategory?.toUpperCase() === 'INVESTMENT') {
      const isFilled = (key: string) => {
        const v = currentStepData[key];
        return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
      };
      if (!isFilled('policyAssigned') || !isFilled('hasBareTrustOrDeed') || !isFilled('uniqueDeathBenefit')) {
        wizardState.markStepError(currentStepId);
        errorSummaryRef.current?.focus();
        return;
      }
      // Valid — skip generic validation and proceed
      wizardState.markStepComplete(currentStepId);
      saveCurrentStep(true);
      wizardState.goNext();
      const nextIdx = wizardState.state.currentStepIndex + 1;
      if (nextIdx < activeSteps.length) {
        storage.updateCurrentStep(activeSteps[nextIdx]);
      }
      validation.clearErrors();
      return;
    }

    // All other steps: validate then continue
    const result = validation.validateStep(currentStepId, currentStepData);
    if (!result.valid) {
      wizardState.markStepError(currentStepId);
      errorSummaryRef.current?.focus();
      return;
    }

    wizardState.markStepComplete(currentStepId);
    saveCurrentStep(true);
    wizardState.goNext();
    const nextIndex = wizardState.state.currentStepIndex + 1;
    if (nextIndex < activeSteps.length) {
      storage.updateCurrentStep(activeSteps[nextIndex]);
    }
    validation.clearErrors();
  }, [currentStepId, currentStepData, wizardState, saveCurrentStep, storage, validation, activeSteps]);

  const handleBack = useCallback(() => {
    saveCurrentStep(false);
    wizardState.goBack();
    const prevIndex = wizardState.state.currentStepIndex - 1;
    if (prevIndex >= 0) {
      storage.updateCurrentStep(activeSteps[prevIndex]);
    }
    validation.clearErrors();
  }, [wizardState, saveCurrentStep, storage, validation, activeSteps]);

  const handleStepClick = useCallback(
    (stepId: WizardStepId) => {
      saveCurrentStep(false);
      wizardState.goToStep(stepId);
      storage.updateCurrentStep(stepId);
      validation.clearErrors();
    },
    [wizardState, saveCurrentStep, storage, validation],
  );

  const handleComplete = useCallback(async () => {
    // Validate all active steps
    const allData = stepDataMap;
    const allResult = validation.validateAllSteps(allData, activeSteps);

    if (!allResult.valid && allResult.firstErrorStep) {
      wizardState.goToStep(allResult.firstErrorStep);
      storage.updateCurrentStep(allResult.firstErrorStep);
      const stepErrors = allResult.stepErrors[allResult.firstErrorStep];
      if (stepErrors) {
        validation.validateStep(allResult.firstErrorStep, allData[allResult.firstErrorStep] || {});
      }
      for (const stepId of Object.keys(allResult.stepErrors) as WizardStepId[]) {
        wizardState.markStepError(stepId);
      }
      return;
    }

    // Build form submission request from all active step data
    const formData: ProviderFormSubmissionRequest = {
      wizardVersion: 2,
      completedSteps: activeSteps.filter((id) => id !== 'loa-validation'),
      formOpenDurationSeconds: formOpenDuration,
      // Auto-set completion date to today
      completionDate: new Date().toISOString().split('T')[0],
    };

    // Flatten all step data
    for (const stepId of activeSteps) {
      const data = allData[stepId];
      if (data) {
        Object.assign(formData, data);
      }
    }

    // Normalize every date field to ISO (YYYY-MM-DD) before submit. The date
    // inputs can leave the visible DD/MM/YYYY string in state (autofill, paste,
    // type toggling); the backend and extraction types expect ISO.
    for (const defs of Object.values(effectiveFieldDefinitions)) {
      if (!defs) continue;
      for (const f of defs) {
        if (f.type !== 'date') continue;
        const v = (formData as Record<string, unknown>)[f.key];
        if (typeof v === 'string' && v) {
          (formData as Record<string, unknown>)[f.key] = toIsoDate(v);
        }
      }
    }

    // Remove internal-only fields
    delete (formData as Record<string, unknown>)['loaAccepted'];

    // Attach the optional transaction history group (extractor output)
    if (transactionHistory && transactionHistory.entries.length > 0) {
      formData.transactionHistory = transactionHistory;
    }

    const success = await onSubmitForm(formData);

    if (success) {
      // Upload pending files
      const uploads = storage.getPendingUploads();
      for (const upload of uploads) {
        const fileKey = upload.fieldKey;
        const files = pendingFiles.get(fileKey);
        if (files) {
          for (const file of files) {
            await onUploadDocument(file, upload.stepId, upload.fieldKey);
          }
        }
      }
      storage.clearStorage();
    }
  }, [
    stepDataMap,
    validation,
    wizardState,
    storage,
    formOpenDuration,
    onSubmitForm,
    onUploadDocument,
    pendingFiles,
    activeSteps,
    transactionHistory,
    effectiveFieldDefinitions,
  ]);

  // ========================================================================
  // Statement extractor — scan handler
  // ========================================================================

  /** Re-evaluate completion for every step using current data + validation. */
  const recomputeAllStatuses = useCallback(
    (data: Partial<Record<WizardStepId, Record<string, unknown>>>) => {
      // Keys injected by the wizard for conditional validation — not real
      // user / extractor data. Ignore them when deciding whether a step
      // has meaningful content, otherwise empty steps get false "error" icons.
      const INJECTED_METADATA_KEYS = new Set(['policyCategory']);

      for (const stepId of activeSteps) {
        if (stepId === 'upload-documents') continue;
        const stepData = data[stepId] || {};
        const meaningfulKeys = Object.keys(stepData).filter(
          (k) => !INJECTED_METADATA_KEYS.has(k),
        );
        const hasMeaningfulData = meaningfulKeys.length > 0;
        const result = validation.validateStep(stepId, stepData, { silent: true });
        if (result.valid && hasMeaningfulData) {
          wizardState.markStepComplete(stepId);
        } else if (hasMeaningfulData) {
          wizardState.markStepError(stepId);
        } else {
          wizardState.markStepIncomplete(stepId);
        }
      }
      validation.clearErrors();
    },
    [activeSteps, validation, wizardState],
  );

  // After the upload step completes, the sidebar expands from
  // [loa-validation, upload-documents] to the full step list.
  // recomputeAllStatuses ran *before* this expansion (during the scan
  // callback), so the newly-visible steps never got their indicators.
  // Re-evaluate now that activeSteps includes everything.
  const prevUploadCompleted = useRef(uploadStepCompleted);
  useEffect(() => {
    if (uploadStepCompleted && !prevUploadCompleted.current) {
      recomputeAllStatuses(stepDataMap);
    }
    prevUploadCompleted.current = uploadStepCompleted;
  }, [uploadStepCompleted, recomputeAllStatuses, stepDataMap]);

  // Auto-advance past upload-documents once it completes.
  // "Skip and continue manually" calls onMarkComplete() then onAdvance()
  // synchronously, but React batches the setUploadStepCompleted(true)
  // state update — so activeSteps hasn't expanded yet when goNext() runs,
  // and navigation fails. This effect fires on the next render when
  // activeSteps includes the full list, completing the navigation.
  const prevUploadCompletedForNav = useRef(uploadStepCompleted);
  useEffect(() => {
    if (
      uploadStepCompleted &&
      !prevUploadCompletedForNav.current &&
      wizardState.state.currentStepId === 'upload-documents'
    ) {
      const uploadIndex = activeSteps.indexOf('upload-documents');
      const nextStepId = activeSteps[uploadIndex + 1];
      if (nextStepId) {
        wizardState.goToStep(nextStepId);
        storage.updateCurrentStep(nextStepId);
      }
    }
    prevUploadCompletedForNav.current = uploadStepCompleted;
  }, [uploadStepCompleted, activeSteps, wizardState, storage]);

  const handleScanForData = useCallback(
    async (files: File[]): Promise<ExtractionRunResult> => {
      const documents: ExtractedDocument[] = [];
      for (const file of files) {
        try {
          documents.push(await extractDocument(file));
        } catch (err) {
          documents.push({
            kind: 'UNKNOWN',
            reason: err instanceof Error ? err.message : 'Failed to read PDF',
            sourceFileName: file.name,
          });
        }
      }

      const patch = applyExtraction(documents);

      // Merge per-step field updates into stepDataMap
      let mergedDataSnapshot: Partial<Record<WizardStepId, Record<string, unknown>>> = {};
      setStepDataMap((prev) => {
        const next = { ...prev };
        for (const [stepId, fields] of Object.entries(patch.stepUpdates)) {
          const id = stepId as WizardStepId;
          next[id] = { ...(next[id] || {}), ...fields };
          // Persist immediately so a refresh doesn't lose the pre-fill
          storage.saveStepData(id, next[id]!, false);
        }
        // Whitelisted providers store the policy detail fields on
        // policy-info, not loa-validation. Mirror the four fields the
        // annual extractor populates.
        if (uploadFlowEnabled) {
          const loa = next['loa-validation'] || {};
          const policyInfoFields: Record<string, unknown> = {};
          for (const key of ['fullPolicyNumber', 'policyCategory', 'pensionType', 'policyStatus'] as const) {
            if (loa[key] !== undefined) policyInfoFields[key] = loa[key];
          }
          if (Object.keys(policyInfoFields).length > 0) {
            next['policy-info'] = { ...(next['policy-info'] || {}), ...policyInfoFields };
            storage.saveStepData('policy-info', next['policy-info']!, false);
          }
        }
        mergedDataSnapshot = next;
        return next;
      });

      // Persist transaction history + auto-filled provenance
      if (patch.transactionHistory) {
        setTransactionHistoryState(patch.transactionHistory);
        storage.setTransactionHistory(patch.transactionHistory);
      }
      const mergedAutoFilled: AutoFilledMap = { ...autoFilled };
      for (const [stepId, fieldMap] of Object.entries(patch.autoFilled)) {
        mergedAutoFilled[stepId as WizardStepId] = {
          ...(mergedAutoFilled[stepId as WizardStepId] || {}),
          ...fieldMap,
        };
      }
      // Mirror provenance for policy-info from loa-validation when whitelisted.
      if (uploadFlowEnabled) {
        const loaProv = mergedAutoFilled['loa-validation'] || {};
        const piProv: Record<string, 'extracted' | 'verified' | 'corrected'> = {};
        for (const key of ['fullPolicyNumber', 'policyCategory', 'pensionType', 'policyStatus'] as const) {
          if (loaProv[key]) piProv[key] = loaProv[key];
        }
        if (Object.keys(piProv).length > 0) {
          mergedAutoFilled['policy-info'] = {
            ...(mergedAutoFilled['policy-info'] || {}),
            ...piProv,
          };
        }
      }
      setAutoFilledState(mergedAutoFilled);
      storage.setAutoFilled(mergedAutoFilled);

      // Re-evaluate completion icons for every step
      recomputeAllStatuses(mergedDataSnapshot);

      // Optional backend reconciliation (no-op if env var unset)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const extractorUrl = (import.meta as any)?.env?.VITE_EXTRACTOR_SERVICE_URL as
        | string
        | undefined;
      if (extractorUrl) {
        for (const file of files) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('frontendExtraction', JSON.stringify(documents));
            await fetch(`${extractorUrl}/api/extractor/standardlife/extract`, {
              method: 'POST',
              body: fd,
            });
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[provider-form] backend extractor reconciliation failed', err);
          }
        }
      }

      const runResult = {
        documents,
        fieldsPopulated: patch.fieldsPopulated,
        transactionsParsed: patch.transactionsParsed,
        warnings: patch.warnings,
      };
      // eslint-disable-next-line no-console
      console.log('[provider-form] statement extraction complete', {
        fieldsPopulated: patch.fieldsPopulated,
        transactionsParsed: patch.transactionsParsed,
        stepUpdates: patch.stepUpdates,
        transactionHistory: patch.transactionHistory,
        sources: patch.sources,
        warnings: patch.warnings,
        documents,
      });
      return runResult;
    },
    [autoFilled, recomputeAllStatuses, storage],
  );

  const handleClearTransactionHistory = useCallback(() => {
    setTransactionHistoryState(undefined);
    storage.setTransactionHistory(undefined);
  }, [storage]);

  // ========================================================================
  // Step Rendering
  // ========================================================================

  const commonStepProps: StepComponentProps = {
    data: currentStepData,
    onFieldChange: handleFieldChange,
    fieldErrors: validation.fieldErrors,
    onClearFieldError: handleClearFieldError,
    pendingFiles,
    onAddFile: handleAddFile,
    onRemoveFile: handleRemoveFile,
  };

  const renderCurrentStep = () => {
    switch (currentStepId) {
      case 'loa-validation':
        return (
          <StepLoaValidation
            {...commonStepProps}
            rejectionReasons={context.rejectionReasons ?? []}
            rejectSubmitting={submitting}
            onReject={onRejectLoa}
            brokerageName={context.brokerageName}
            policyTypeName={context.policyTypeName}
            policyTypeCategory={context.policyTypeCategory}
            clientName={context.clientName}
            advisorName={context.advisorName}
            hidePolicyDetails={uploadFlowEnabled || context.policyTypeCategory?.toUpperCase() === 'INVESTMENT'}
          />
        );
      case 'policy-info':
        return (
          <StepLoaValidation
            {...commonStepProps}
            rejectionReasons={context.rejectionReasons ?? []}
            rejectSubmitting={submitting}
            onReject={onRejectLoa}
            brokerageName={context.brokerageName}
            policyTypeName={context.policyTypeName}
            policyTypeCategory={context.policyTypeCategory}
            clientName={context.clientName}
            advisorName={context.advisorName}
            policyOnly
          />
        );
      case 'provider-details':
        return <StepProviderDetails {...commonStepProps} policyTypeCategory={context.policyTypeCategory} />;
      case 'policyholder-details':
        return <StepPolicyholderDetails {...commonStepProps} />;
      case 'policy-details':
        return <StepPolicyDetails {...commonStepProps} policyTypeCategory={context.policyTypeCategory} onPolicyTypeChange={handlePolicyTypeChange} />;
      case 'employment-details':
        return <StepEmploymentDetails {...commonStepProps} showSeveranceSubSection={showSeveranceSubSection} />;
      case 'contribution-details':
        return (
          <StepContributionDetails
            {...commonStepProps}
            autoFilledFields={autoFilled['contribution-details']}
          />
        );
      case 'transfers-in':
        return <StepTransfersIn {...commonStepProps} />;
      case 'crystallised-retired':
        return <StepCrystallisedRetired {...commonStepProps} />;
      case 'uploads':
        return <StepUploads {...commonStepProps} pendingFiles={pendingFiles} onAddFile={handleAddFile} onRemoveFile={handleRemoveFile} policyType={selectedPolicyType} />;
      case 'investments':
        return <StepInvestments {...commonStepProps} />;
      case 'charges-fees':
        return <StepChargesFees {...commonStepProps} policyTypeCategory={context.policyTypeCategory} />;
      case 'other-charges-details':
        return <StepOtherChargesDetails {...commonStepProps} policyTypeCategory={context.policyTypeCategory} />;
      case 'transaction-history':
        // Whitelisted providers: show the parsed transactions table from
        // the scan step. Other providers keep the manual file upload.
        if (uploadFlowEnabled) {
          return (
            <div className="flex flex-col gap-lg">
              <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
                Transaction History
              </div>
              {transactionHistory && transactionHistory.entries.length > 0 ? (
                <TransactionHistoryTable
                  history={transactionHistory}
                  onClear={handleClearTransactionHistory}
                />
              ) : (
                <div className="text-grey-04 text-body-03">
                  No transactions have been extracted yet. Upload a
                  transactions PDF in the Upload Documents step and click
                  Scan for data.
                </div>
              )}
            </div>
          );
        }
        return <StepTransactionHistory {...commonStepProps} />;
      case 'other-information':
        return <StepOtherInformation {...commonStepProps} policyTypeCategory={context.policyTypeCategory} />;
      case 'upload-documents':
        return (
          <StepUploadDocuments
            {...commonStepProps}
            onScanForData={handleScanForData}
            onMarkComplete={() => {
              wizardState.markStepComplete('upload-documents');
              storage.saveStepData('upload-documents', stepDataMap['upload-documents'] || {}, true);
              setUploadStepCompleted(true);
            }}
            onAdvance={handleSaveAndContinue}
          />
        );
      case 'associated-benefits':
        return <StepAssociatedBenefits {...commonStepProps} />;
      default:
        return null;
    }
  };

  if (!context.submissionAllowed) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
        <p className="font-semibold mb-1">No Submissions Available</p>
        <p>The submission limit for this access link has been reached.</p>
      </div>
    );
  }

  return (
    <WizardLayout context={context}>
      <WizardHeader
        context={context}
        lastSavedAt={storage.storageData?.lastUpdatedAt ?? null}
      />

      <WizardProgressBar
        currentStepIndex={wizardState.state.currentStepIndex}
        totalSteps={activeSteps.length}
        currentStepName={WIZARD_STEP_NAMES[currentStepId]}
        onExpandSidebar={() => setMobileSidebarOpen(true)}
      />

      <div className="wizard-body">
        <WizardSidebar
          steps={wizardState.state.steps}
          currentStepId={currentStepId}
          onStepClick={handleStepClick}
          canNavigateTo={wizardState.canNavigateTo}
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
        />

        <WizardStepContent
          footer={wizardState.state.isLastStep ? (
            <WizardCompleteFooter
              currentStepId={currentStepId}
              isFirstStep={wizardState.state.isFirstStep}
              isLastStep={wizardState.state.isLastStep}
              isSaving={storage.isSaving}
              isSubmitting={submitting || submission.submitting}
              isPolicyClosed
              stepValid={(() => {
                // For crystallised-retired: also validate additional lump sum entries
                if (currentStepId === 'crystallised-retired') {
                  const fields = STEP_FIELD_DEFINITIONS[currentStepId];
                  const baseValid = !fields || fields.every(f => {
                    const isRequired = f.required || (f.requiredWhen && currentStepData[f.requiredWhen.field] === f.requiredWhen.value);
                    const val = currentStepData[f.key];
                    const isEmpty = val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
                    if (isRequired && isEmpty) return false;
                    return true;
                  });
                  if (!baseValid) return false;
                  // Validate additional lump sum entries when previous lump sums = Yes
                  if ((currentStepData.crystallisedPreviousTaxFreeLumpSums as string) === 'Yes') {
                    const entries = (currentStepData.crystallisedAdditionalLumpSums as { amount?: string; taxPaid?: string; date?: string }[]) || [];
                    if (entries.length === 0) return false;
                    const allFilled = entries.every(e =>
                      e.amount && e.amount.trim() !== '' &&
                      e.taxPaid && e.taxPaid.trim() !== '' &&
                      e.date && e.date.trim() !== ''
                    );
                    if (!allFilled) return false;
                  }
                  return true;
                }
                // For associated-benefits: validate dynamic benefit cards
                if (currentStepId === 'associated-benefits') {
                  const hasBenefits = currentStepData.hasProtectionBenefits as string;
                  if (!hasBenefits) return false;
                  if (hasBenefits !== 'Yes') return true;
                  const lcEnabled = !!currentStepData.lifeCoverEnabled;
                  const ipEnabled = !!currentStepData.incomeProtectionEnabled;
                  const siEnabled = !!currentStepData.specifiedIllnessEnabled;
                  if (!lcEnabled && !ipEnabled && !siEnabled) return false;
                  const cardRequiredFields: string[] = ['typeCover', 'nameCovered', 'policyRefNumber', 'salaryOnRecord', 'nraCover', 'premiumFromPension'];
                  const siRequiredFields: string[] = ['nameCovered', 'policyRefNumber', 'specifiedIllnessCover', 'termOfCover', 'startDateOfCover', 'endDateOfCover', 'coverInForce', 'nraCover', 'specifiedIllnessExclusions', 'continuationOptionDetails', 'whoPaysThePremium', 'indexLinked'];
                  const validateCards = (cardsKey: string, extraField: string): boolean => {
                    const cards = (currentStepData[cardsKey] as Record<string, unknown>[]) || [];
                    if (cards.length === 0) return false;
                    return cards.every(card => {
                      const fields = [...cardRequiredFields, extraField];
                      if (card.typeCover === 'Death in Service' || card.typeCover === 'Executive Income Protection') fields.push('continuationOptionDetails');
                      return fields.every(f => {
                        const v = card[f];
                        return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                      });
                    });
                  };
                  const validateSiCards = (): boolean => {
                    const cards = (currentStepData.specifiedIllnessCards as Record<string, unknown>[]) || [];
                    if (cards.length === 0) return false;
                    return cards.every(card => siRequiredFields.every(f => {
                      const v = card[f];
                      return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                    }));
                  };
                  if (lcEnabled && !validateCards('lifeCoverCards', 'timesSalary')) return false;
                  if (ipEnabled && !validateCards('incomeProtectionCards', 'percentOfSalary')) return false;
                  if (siEnabled && !validateSiCards()) return false;
                  return true;
                }
                // For transfers-in: custom nested conditional validation
                if (currentStepId === 'transfers-in') {
                  const hasTransfers = currentStepData.hasReceivedTransfers as string;
                  if (!hasTransfers) return false;
                  if (hasTransfers !== 'Yes') return true;
                  const isFilled = (key: string) => { const v = currentStepData[key]; return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== ''); };
                  if (!isFilled('detailAmountTransferred') || !isFilled('detailProvider') || !isFilled('detailDateTransferred') || !isFilled('detailPolicyType') || !isFilled('detailReferenceNumber') || !isFilled('detailSchemeName') || !isFilled('sameEmployment')) return false;
                  const sameEmp = currentStepData.sameEmployment as string;
                  if (sameEmp === 'Yes') {
                    if (!isFilled('sameEmpDateTransferred') || !isFilled('sameEmpAmountTransferred') || !isFilled('employeeContributionsTransferred') || !isFilled('employerContributionsTransferred') || !isFilled('previousProviderName') || !isFilled('previousPolicyNumber') || !isFilled('transferType')) return false;
                  }
                  if (sameEmp === 'No') {
                    if (!isFilled('previousProvidersName') || !isFilled('previousPolicyNumberNo') || !isFilled('employerDetails') || !isFilled('transferDateJoinedService') || !isFilled('transferDateJoinedScheme') || !isFilled('transferDateOfLeavingService') || !isFilled('salaryAtLeavingService') || !isFilled('totalEmployerContributions') || !isFilled('totalEmployeeContributions') || !isFilled('transferFromOverseas')) return false;
                    if ((currentStepData.transferFromOverseas as string) === 'Yes') {
                      if (!isFilled('overseasCountry') || !isFilled('wasTaxPaid')) return false;
                      if ((currentStepData.wasTaxPaid as string) === 'Yes' && !isFilled('overseasTaxDetails')) return false;
                    }
                  }
                  return true;
                }
                // For investments: funds are optional, but if added all required fields must be filled
                if (currentStepId === 'investments') {
                  const funds = (currentStepData.investmentFunds as Record<string, unknown>[]) || [];
                  if (funds.length === 0) return true;
                  return funds.every(fund => INVESTMENT_FUND_REQUIRED_FIELDS.every(f => {
                    const v = fund[f];
                    return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                  }));
                }
                // Investment other-information: only 3 dropdowns are required
                if (currentStepId === 'other-information' && context.policyTypeCategory?.toUpperCase() === 'INVESTMENT') {
                  const isFilled = (key: string) => {
                    const v = currentStepData[key];
                    return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                  };
                  return isFilled('policyAssigned') && isFilled('hasBareTrustOrDeed') && isFilled('uniqueDeathBenefit');
                }
                // Investment policy-details: validate investment-specific required fields
                if (currentStepId === 'policy-details' && context.policyTypeCategory?.toUpperCase() === 'INVESTMENT') {
                  const isFilled = (key: string) => {
                    const v = currentStepData[key];
                    return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                  };
                  if (!isFilled('fullPolicyNumber') || !isFilled('policyType')) return false;
                  if (!isFilled('currentPolicyValue') || !isFilled('dateCurrentPolicyValue')) return false;
                  if (!isFilled('transferSurrenderValue') || !isFilled('dateTransferSurrenderValue')) return false;
                  if (!isFilled('exitPenaltyDetails')) return false;
                  return true;
                }
                const fields = STEP_FIELD_DEFINITIONS[currentStepId];
                if (!fields) return true;
                return fields.every(f => {
                  const isRequired = f.required || (f.requiredWhen && currentStepData[f.requiredWhen.field] === f.requiredWhen.value);
                  const val = currentStepData[f.key];
                  const isEmpty = val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
                  if (isRequired && isEmpty) return false;
                  if (isEmpty) return true;
                  if (f.type === 'email' && typeof val === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return false;
                  return true;
                });
              })()}
              onBack={handleBack}
              onSave={handleSave}
              onSaveAndContinue={handleSaveAndContinue}
              onComplete={handleComplete}
            />
          ) : undefined}
        >
          <WizardStorageBanner visible={!storage.storageAvailable} />

          {validation.hasErrors && (
            <WizardErrorSummary errors={validation.errorSummary} errorSummaryRef={errorSummaryRef} />
          )}

          {submission.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <p className="font-semibold mb-1">Submission Error</p>
              <p>{submission.error}</p>
            </div>
          )}

          {renderCurrentStep()}

          {/* Inline footer (inside form card) — only for non-closed or non-last steps */}
          {!(selectedPolicyStatus === 'CLOSED' && wizardState.state.isLastStep) && (
            <WizardFooter
              currentStepId={currentStepId}
              isFirstStep={wizardState.state.isFirstStep}
              isLastStep={wizardState.state.isLastStep}
              isSaving={storage.isSaving}
              isSubmitting={submitting || submission.submitting}
              loaAccepted={currentStepData.loaAccepted as boolean | undefined}
              isPolicyClosed={selectedPolicyStatus === 'CLOSED'}
              stepValid={(() => {
                // For loa-validation: validate policy fields + closed sub-fields
                if (currentStepId === 'loa-validation') {
                  if (currentStepData.loaAccepted !== true) return false;
                  // Investment policies only need loaAccepted.
                  // Whitelisted providers hide the Policy Details section, so
                  // accepting the LOA is sufficient to enable Save & Continue.
                  if (context.policyTypeCategory?.toUpperCase() === 'INVESTMENT' || uploadFlowEnabled) return true;
                  const fields = STEP_FIELD_DEFINITIONS[currentStepId];
                  if (!fields) return true;
                  // Check required + conditionally required fields
                  const baseValid = fields.every(f => {
                    const isRequired = f.required || (f.requiredWhen && currentStepData[f.requiredWhen.field] === f.requiredWhen.value);
                    if (!isRequired) return true;
                    const val = currentStepData[f.key];
                    if (typeof val === 'string') return val.trim().length > 0;
                    return val !== undefined && val !== null;
                  });
                  if (!baseValid) return false;

                  // If closed, validate sub-fields based on closed reason
                  if (currentStepData.policyStatus === 'CLOSED' && currentStepData.policyClosedReason) {
                    const prefix = currentStepData.policyClosedReason === 'RETIRED' ? 'retired' : 'transferred';
                    const closedRequiredFields = [
                      `${prefix}ProviderName`,
                      `${prefix}DateOfTransfer`,
                      `${prefix}PolicyReference`,
                      `${prefix}PolicyType`,
                      `${prefix}TransferType`,
                      `${prefix}TransferValue`,
                    ];
                    // Retired-specific fields
                    if (currentStepData.policyClosedReason === 'RETIRED') {
                      closedRequiredFields.push('retiredBceDate', 'retiredBceValue', 'retiredTaxFreeLumpSum');
                    }
                    const closedValid = closedRequiredFields.every(key => {
                      const val = currentStepData[key];
                      if (typeof val === 'string') return val.trim().length > 0;
                      return val !== undefined && val !== null;
                    });
                    if (!closedValid) return false;

                    // Total contributions required for Transferred only, unless policy type is ARF or Vested PRSA
                    const pType = currentStepData[`${prefix}PolicyType`] as string;
                    if (currentStepData.policyClosedReason !== 'RETIRED' && pType && pType !== 'ARF' && pType !== 'VESTED_PRSA') {
                      const contribValid = ['totalEmployerContributions', 'totalEmployeeContributions'].every(key => {
                        const val = currentStepData[key];
                        if (typeof val === 'string') return val.trim().length > 0;
                        return val !== undefined && val !== null;
                      });
                      if (!contribValid) return false;
                    }
                  }
                  return true;
                }
                // For policy-info (whitelisted): same field/closed validation as
                // legacy loa-validation, minus the loaAccepted gate.
                if (currentStepId === 'policy-info') {
                  const fields = STEP_FIELD_DEFINITIONS[currentStepId];
                  if (!fields) return true;
                  const baseValid = fields.every(f => {
                    const isRequired = f.required || (f.requiredWhen && currentStepData[f.requiredWhen.field] === f.requiredWhen.value);
                    if (!isRequired) return true;
                    const val = currentStepData[f.key];
                    if (typeof val === 'string') return val.trim().length > 0;
                    return val !== undefined && val !== null;
                  });
                  if (!baseValid) return false;
                  if (currentStepData.policyStatus === 'CLOSED' && currentStepData.policyClosedReason) {
                    const prefix = currentStepData.policyClosedReason === 'RETIRED' ? 'retired' : 'transferred';
                    const closedRequiredFields = [
                      `${prefix}ProviderName`,
                      `${prefix}DateOfTransfer`,
                      `${prefix}PolicyReference`,
                      `${prefix}PolicyType`,
                      `${prefix}TransferType`,
                      `${prefix}TransferValue`,
                    ];
                    if (currentStepData.policyClosedReason === 'RETIRED') {
                      closedRequiredFields.push('retiredBceDate', 'retiredBceValue', 'retiredTaxFreeLumpSum');
                    }
                    const closedValid = closedRequiredFields.every(key => {
                      const val = currentStepData[key];
                      if (typeof val === 'string') return val.trim().length > 0;
                      return val !== undefined && val !== null;
                    });
                    if (!closedValid) return false;
                    const pType = currentStepData[`${prefix}PolicyType`] as string;
                    if (currentStepData.policyClosedReason !== 'RETIRED' && pType && pType !== 'ARF' && pType !== 'VESTED_PRSA') {
                      const contribValid = ['totalEmployerContributions', 'totalEmployeeContributions'].every(key => {
                        const val = currentStepData[key];
                        if (typeof val === 'string') return val.trim().length > 0;
                        return val !== undefined && val !== null;
                      });
                      if (!contribValid) return false;
                    }
                  }
                  return true;
                }
                // For crystallised-retired: also validate additional lump sum entries
                if (currentStepId === 'crystallised-retired') {
                  const fields2 = STEP_FIELD_DEFINITIONS[currentStepId];
                  const baseValid2 = !fields2 || fields2.every(f => {
                    const isRequired = f.required || (f.requiredWhen && currentStepData[f.requiredWhen.field] === f.requiredWhen.value);
                    const val = currentStepData[f.key];
                    const isEmpty = val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
                    if (isRequired && isEmpty) return false;
                    return true;
                  });
                  if (!baseValid2) return false;
                  if ((currentStepData.crystallisedPreviousTaxFreeLumpSums as string) === 'Yes') {
                    const entries2 = (currentStepData.crystallisedAdditionalLumpSums as { amount?: string; taxPaid?: string; date?: string }[]) || [];
                    if (entries2.length === 0) return false;
                    const allFilled2 = entries2.every(e =>
                      e.amount && e.amount.trim() !== '' &&
                      e.taxPaid && e.taxPaid.trim() !== '' &&
                      e.date && e.date.trim() !== ''
                    );
                    if (!allFilled2) return false;
                  }
                  return true;
                }
                // For associated-benefits: validate dynamic benefit cards
                if (currentStepId === 'associated-benefits') {
                  const hasBenefits = currentStepData.hasProtectionBenefits as string;
                  if (!hasBenefits) return false;
                  if (hasBenefits !== 'Yes') return true;
                  // At least one checkbox must be enabled
                  const lcEnabled = !!currentStepData.lifeCoverEnabled;
                  const ipEnabled = !!currentStepData.incomeProtectionEnabled;
                  const siEnabled = !!currentStepData.specifiedIllnessEnabled;
                  if (!lcEnabled && !ipEnabled && !siEnabled) return false;
                  // Validate all cards in each enabled category
                  const cardRequiredFields: (keyof Record<string, unknown>)[] = ['typeCover', 'nameCovered', 'policyRefNumber', 'salaryOnRecord', 'nraCover', 'premiumFromPension'];
                  const siRequiredFields2: string[] = ['nameCovered', 'policyRefNumber', 'specifiedIllnessCover', 'termOfCover', 'startDateOfCover', 'endDateOfCover', 'coverInForce', 'nraCover', 'specifiedIllnessExclusions', 'continuationOptionDetails', 'whoPaysThePremium', 'indexLinked'];
                  const validateCards = (cardsKey: string, extraField: string): boolean => {
                    const cards = (currentStepData[cardsKey] as Record<string, unknown>[]) || [];
                    if (cards.length === 0) return false;
                    return cards.every(card => {
                      const allFields = [...cardRequiredFields, extraField];
                      if (card.typeCover === 'Death in Service' || card.typeCover === 'Executive Income Protection') allFields.push('continuationOptionDetails');
                      return allFields.every(f => {
                        const v = card[f];
                        return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                      });
                    });
                  };
                  const validateSiCards2 = (): boolean => {
                    const cards = (currentStepData.specifiedIllnessCards as Record<string, unknown>[]) || [];
                    if (cards.length === 0) return false;
                    return cards.every(card => siRequiredFields2.every(f => {
                      const v = card[f];
                      return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                    }));
                  };
                  if (lcEnabled && !validateCards('lifeCoverCards', 'timesSalary')) return false;
                  if (ipEnabled && !validateCards('incomeProtectionCards', 'percentOfSalary')) return false;
                  if (siEnabled && !validateSiCards2()) return false;
                  return true;
                }
                // For transfers-in: custom nested conditional validation
                if (currentStepId === 'transfers-in') {
                  const hasTransfers = currentStepData.hasReceivedTransfers as string;
                  if (!hasTransfers) return false;
                  if (hasTransfers !== 'Yes') return true;
                  const isFilled = (key: string) => { const v = currentStepData[key]; return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== ''); };
                  if (!isFilled('detailAmountTransferred') || !isFilled('detailProvider') || !isFilled('detailDateTransferred') || !isFilled('detailPolicyType') || !isFilled('detailReferenceNumber') || !isFilled('detailSchemeName') || !isFilled('sameEmployment')) return false;
                  const sameEmp = currentStepData.sameEmployment as string;
                  if (sameEmp === 'Yes') {
                    if (!isFilled('sameEmpDateTransferred') || !isFilled('sameEmpAmountTransferred') || !isFilled('employeeContributionsTransferred') || !isFilled('employerContributionsTransferred') || !isFilled('previousProviderName') || !isFilled('previousPolicyNumber') || !isFilled('transferType')) return false;
                  }
                  if (sameEmp === 'No') {
                    if (!isFilled('previousProvidersName') || !isFilled('previousPolicyNumberNo') || !isFilled('employerDetails') || !isFilled('transferDateJoinedService') || !isFilled('transferDateJoinedScheme') || !isFilled('transferDateOfLeavingService') || !isFilled('salaryAtLeavingService') || !isFilled('totalEmployerContributions') || !isFilled('totalEmployeeContributions') || !isFilled('transferFromOverseas')) return false;
                    if ((currentStepData.transferFromOverseas as string) === 'Yes') {
                      if (!isFilled('overseasCountry') || !isFilled('wasTaxPaid')) return false;
                      if ((currentStepData.wasTaxPaid as string) === 'Yes' && !isFilled('overseasTaxDetails')) return false;
                    }
                  }
                  return true;
                }
                // For investments: funds are optional, but if added all required fields must be filled
                if (currentStepId === 'investments') {
                  const funds = (currentStepData.investmentFunds as Record<string, unknown>[]) || [];
                  if (funds.length === 0) return true;
                  return funds.every(fund => INVESTMENT_FUND_REQUIRED_FIELDS.every(f => {
                    const v = fund[f];
                    return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                  }));
                }
                // Investment other-information: only 3 dropdowns are required
                if (currentStepId === 'other-information' && context.policyTypeCategory?.toUpperCase() === 'INVESTMENT') {
                  const isFilled = (key: string) => {
                    const v = currentStepData[key];
                    return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                  };
                  return isFilled('policyAssigned') && isFilled('hasBareTrustOrDeed') && isFilled('uniqueDeathBenefit');
                }
                // Investment policy-details: validate investment-specific required fields
                if (currentStepId === 'policy-details' && context.policyTypeCategory?.toUpperCase() === 'INVESTMENT') {
                  const isFilled = (key: string) => {
                    const v = currentStepData[key];
                    return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');
                  };
                  if (!isFilled('fullPolicyNumber') || !isFilled('policyType')) return false;
                  if (!isFilled('currentPolicyValue') || !isFilled('dateCurrentPolicyValue')) return false;
                  if (!isFilled('transferSurrenderValue') || !isFilled('dateTransferSurrenderValue')) return false;
                  if (!isFilled('exitPenaltyDetails')) return false;
                  return true;
                }
                const fields = STEP_FIELD_DEFINITIONS[currentStepId];
                if (!fields) return true;
                return fields.every(f => {
                  const isRequired = f.required || (f.requiredWhen && currentStepData[f.requiredWhen.field] === f.requiredWhen.value);
                  const val = currentStepData[f.key];
                  const isEmpty = val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
                  if (isRequired && isEmpty) return false;
                  if (isEmpty) return true;
                  if (f.type === 'email' && typeof val === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return false;
                  return true;
                });
              })()}
              onBack={handleBack}
              onSave={handleSave}
              onSaveAndContinue={handleSaveAndContinue}
              onComplete={handleComplete}
            />
          )}
        </WizardStepContent>
      </div>

      <WizardSaveToast visible={storage.isSaving} />
    </WizardLayout>
  );
}
